<script lang="ts" setup>
  import { format, parse } from 'date-fns'

  import type { Nullable } from '~/core/types'
  import type { SlotAvailable } from '~/core/types/courses'

  type Props = {
    availability: SlotAvailable
    selectedId?: Nullable<number>
  }

  const props = defineProps<Props>()
  const { t } = useI18n()

  const date = computed(() => format(parse(props.availability.date, 'HH:mm:ss', new Date()), 'HH\'h\'mm'))

  const percentage = computed(() => props.availability.percentage ?? 0)

</script>
<template>
  <div
    class="c-availability"
    :class="{ selected: availability.id === selectedId }"
  >
    <div class="left">
      <span class="date">{{ date }}</span>
      <span class="free-places">
        {{ availability.free_places }}&nbsp;{{ t("golf.available-slot", availability.free_places) }}
      </span>
    </div>
    <div class="right">
      <div class="prices">
        <div class="sold-price">
          <span
            v-if="availability.base_price !== availability.final_price"
            class="base-price"
          >{{ availability.base_price }}€</span>
          <span
            v-if="percentage"
            class="percentage"
          >{{ percentage.toFixed(2) }}%</span>
        </div>
        <div class="final-price">
          <span>{{ availability.final_price }}€</span>
        </div>
        <SvgIcon
          v-if="availability.has_promotions"
          icon="promotion-colored"
          :width="16"
          :height="16"
          :desktop-height="16"
          :desktop-width="16"
        />
        <SvgIcon
          v-if="availability.is_damaged"
          icon="cone"
          :width="16"
          :height="16"
          :desktop-height="16"
          :desktop-width="16"
        />
      </div>
      <p
        v-if="availability.promotion_title"
        class="promotion-title"
        :title="availability.promotion_title"
      >
        {{ availability.promotion_title }}
      </p>
      <p
        v-if="availability.is_damaged"
        class="damaged-title"
        :title="availability.damaged_title ?? ''"
      >
        {{ availability.damaged_title }}
      </p>
    </div>
  </div>
</template>
<style scoped>

.c-availability {
  display: flex;
  height: 60px;
  flex: 1 1 auto;
  flex-direction: row;
  padding: 0.375em 0.625em;
  border: solid 2px white;
  border-radius: 4px;
  box-shadow: var(--box-shadow-2);
  cursor: pointer;
  transition: background-color ease-in 0.1s, border-color ease-in 0.1s;

  .left {
    display: flex;
    flex: 1 1 50%;
    flex-direction: column;
    justify-content: space-between;

    .date {
      margin-top: 0.125em;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: bold;
    }

    .free-places {
      display: inline-block;
      overflow: hidden;
      max-width: 120px;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 0.75em;
      font-weight: 500;
      opacity: 0.7;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .right {
    display: flex;
    flex: 1 1 50%;
    flex-direction: column;
    justify-content: space-between;

    .prices {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;

      .sold-price {
        display: flex;
        flex-direction: column;
        margin-right: 0.25em;

        .base-price {
          padding-left: 4px;
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 0.625em;
          font-weight: 500;
          line-height: 1em;
          opacity: 0.5;
          text-decoration: line-through;
        }

        .percentage {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 0.625em;
          font-weight: 500;
          line-height: 1em;
          white-space: nowrap;

          &::before {
            display: inline-block;
            width: 4px;
            content: "-";
          }
        }
      }

      .final-price {
        margin-right: 0.5em;

        span {
          color: var(--color-primary);
          font-family: Open-Sans, sans-serif;
          font-size: 1.5em;
          font-weight: bold;
          line-height: 1em;
        }
      }
    }

    .promotion-title,
    .damaged-title {
      display: inline-block;
      overflow: hidden;
      max-width: 80px;
      align-self: flex-end;
      margin-left: 0.25em;
      color: var(--color-promo);
      font-family: Open-Sans, sans-serif;
      font-size: 0.75em;
      font-weight: 500;
      text-align: right;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &.selected {
    border: solid 2px var(--color-primary);
    background-color: var(--color-primary-light);
  }
}
</style>
